import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase-config';

const Gallery = () => {
  const [media, setMedia] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const q = query(collection(db, 'gallery'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const data = [];
        snapshot.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
        setMedia(data);
      } catch (error) {
        console.error('Error loading gallery:', error);
      }
    };
    loadGallery();
  }, []);

  const filteredMedia = filter === 'all' ? media : media.filter(item => item.type === filter);
  const imageCount = media.filter(item => item.type === 'image').length;
  const videoCount = media.filter(item => item.type === 'video').length;

  return (
    <div className="container gallery-page">
      <motion.h1
        className="gallery-page-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <i className="fas fa-images" style={{ color: 'var(--clr-blue)' }}></i> Campus Gallery
      </motion.h1>
      <p className="gallery-page-subtitle">Explore our vibrant campus life through captivating moments and events</p>

      {/* Stats */}
      <div className="gallery-stats">
        <div className="gallery-stat-card stat-blue">
          <div className="gallery-stat-value">{media.length}</div>
          <div className="gallery-stat-label">Total Media</div>
        </div>
        <div className="gallery-stat-card stat-purple">
          <div className="gallery-stat-value">{imageCount}</div>
          <div className="gallery-stat-label">Photos</div>
        </div>
        <div className="gallery-stat-card stat-emerald">
          <div className="gallery-stat-value">{videoCount}</div>
          <div className="gallery-stat-label">Videos</div>
        </div>
      </div>

      {/* Filters */}
      <div className="gallery-filters">
        {[
          { value: 'all', label: 'All Media', icon: 'fa-images' },
          { value: 'image', label: 'Photos', icon: 'fa-image' },
          { value: 'video', label: 'Videos', icon: 'fa-video' }
        ].map(btn => (
          <button
            key={btn.value}
            onClick={() => setFilter(btn.value)}
            className={`gallery-filter-btn ${filter === btn.value ? 'active' : ''}`}
          >
            <i className={`fas ${btn.icon}`}></i> {btn.label}
          </button>
        ))}
      </div>

      {/* Gallery Grid */}
      <div className="gallery-grid">
        {filteredMedia.length === 0 ? (
          <div className="gallery-empty">
            <i className="fas fa-images"></i>
            <p>No media found in this category</p>
          </div>
        ) : (
          filteredMedia.map((item, index) => {
            const isVideo = item.type === 'video';
            return (
              <motion.div
                key={item.id}
                className="gallery-item"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -8, boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)' }}
                onClick={() => setSelectedMedia(item)}
              >
                <div className="gallery-item-media">
                  {isVideo ? (
                    <>
                      <video src={item.url} muted preload="metadata" />
                      <div className="gallery-play-overlay">
                        <i className="fas fa-play"></i>
                      </div>
                    </>
                  ) : (
                    <img src={item.url} alt={item.title} />
                  )}
                  <div className={`gallery-item-badge ${isVideo ? 'video-badge' : 'image-badge'}`}>
                    <i className={`fas ${isVideo ? 'fa-video' : 'fa-image'}`}></i> {isVideo ? 'Video' : 'Photo'}
                  </div>
                </div>
                <div className="gallery-item-content">
                  <h3>{item.title || 'Untitled'}</h3>
                  <span className="gallery-item-album">
                    <i className="fas fa-folder"></i> {item.album || 'General'}
                  </span>
                  <div className="gallery-item-date">
                    <i className="far fa-calendar-alt"></i> {item.createdAt?.toDate().toLocaleDateString() || 'Recent'}
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {selectedMedia && (
        <div
          className="gallery-modal"
          onClick={() => setSelectedMedia(null)}
        >
          <button
            className="gallery-modal-close"
            onClick={() => setSelectedMedia(null)}
          >
            <i className="fas fa-times"></i>
          </button>
          <div className="gallery-modal-content">
            {selectedMedia.type === 'video' ? (
              <video src={selectedMedia.url} controls autoPlay />
            ) : (
              <img src={selectedMedia.url} alt={selectedMedia.title} />
            )}
          </div>
        </div>
      )}

      {/* Add CSS for responsiveness */}
      <style>{`
        /* Gallery Page Styles */
        .gallery-page {
          padding-top: 40px;
        }

        .gallery-page-title {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          margin-bottom: 10px;
        }

        .gallery-page-subtitle {
          color: var(--text-secondary);
          margin-bottom: 30px;
        }

        /* Stats */
        .gallery-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 40px;
        }

        .gallery-stat-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          borderRadius: 16px;
          padding: 24px;
          text-align: center;
          border-bottom: 3px solid var(--clr-blue);
        }

        .stat-blue {
          border-bottom-color: var(--clr-blue);
        }
        .stat-purple {
          border-bottom-color: var(--clr-purple);
        }
        .stat-emerald {
          border-bottom-color: var(--clr-emerald);
        }

        .gallery-stat-value {
          font-family: 'Playfair Display', serif;
          font-size: 2rem;
          font-weight: 800;
        }

        .stat-blue .gallery-stat-value {
          color: var(--clr-blue);
        }
        .stat-purple .gallery-stat-value {
          color: var(--clr-purple);
        }
        .stat-emerald .gallery-stat-value {
          color: var(--clr-emerald);
        }

        .gallery-stat-label {
          font-size: 0.85rem;
          font-weight: 700;
          margin-top: 5px;
        }

        /* Filters */
        .gallery-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 30px;
          justify-content: center;
        }

        .gallery-filter-btn {
          padding: 12px 28px;
          background: rgba(255, 255, 255, 0.4);
          backdrop-filter: blur(20px) saturate(140%);
          border: 1px solid var(--clr-blue-border);
          border-radius: 50px;
          color: var(--text-secondary);
          font-weight: 700;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .gallery-filter-btn.active {
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          color: white;
          border: none;
        }

        .gallery-filter-btn:hover:not(.active) {
          background: rgba(255, 255, 255, 0.7);
        }

        /* Gallery Grid */
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 28px;
          margin-bottom: 60px;
        }

        .gallery-item {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid var(--clr-blue-border);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .gallery-item-media {
          position: relative;
          height: 260px;
          overflow: hidden;
          background: linear-gradient(135deg, #0f172a, #1e293b);
        }

        .gallery-item-media img,
        .gallery-item-media video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s;
        }

        .gallery-item:hover .gallery-item-media img,
        .gallery-item:hover .gallery-item-media video {
          transform: scale(1.05);
        }

        .gallery-play-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 60px;
          height: 60px;
          background: rgba(0, 0, 0, 0.6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.8rem;
        }

        .gallery-item-badge {
          position: absolute;
          top: 15px;
          right: 15px;
          padding: 6px 14px;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 700;
          color: white;
        }

        .image-badge {
          background: var(--clr-blue);
        }
        .video-badge {
          background: var(--clr-emerald);
        }

        .gallery-item-content {
          padding: 20px;
        }

        .gallery-item-content h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.1rem;
          margin-bottom: 8px;
        }

        .gallery-item-album {
          display: inline-block;
          padding: 4px 12px;
          background: rgba(14, 165, 233, 0.1);
          border-radius: 20px;
          font-size: 0.7rem;
          color: var(--clr-blue);
          font-weight: 600;
        }

        .gallery-item-date {
          font-size: 0.75rem;
          color: var(--text-muted);
          margin-top: 10px;
        }

        .gallery-empty {
          grid-column: 1 / -1;
          text-align: center;
          padding: 60px;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 24px;
        }

        .gallery-empty i {
          font-size: 2rem;
          display: block;
          margin-bottom: 10px;
        }

        /* Modal */
        .gallery-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.95);
          z-index: 10001;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .gallery-modal-close {
          position: absolute;
          top: 20px;
          right: 30px;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          width: 45px;
          height: 45px;
          border-radius: 50%;
          color: white;
          font-size: 1.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .gallery-modal-close:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .gallery-modal-content {
          max-width: 90vw;
          max-height: 90vh;
        }

        .gallery-modal-content img,
        .gallery-modal-content video {
          max-width: 90vw;
          max-height: 85vh;
          object-fit: contain;
          border-radius: 8px;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .gallery-page {
            padding-top: 20px;
          }

          .gallery-page-title {
            font-size: 1.8rem;
          }

          .gallery-stats {
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
            margin-bottom: 25px;
          }

          .gallery-stat-card {
            padding: 16px 12px;
          }

          .gallery-stat-value {
            font-size: 1.5rem;
          }

          .gallery-stat-label {
            font-size: 0.7rem;
          }

          .gallery-filters {
            gap: 10px;
            margin-bottom: 25px;
          }

          .gallery-filter-btn {
            padding: 10px 20px;
            font-size: 0.8rem;
          }

          .gallery-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 20px;
          }

          .gallery-item-media {
            height: 220px;
          }

          .gallery-play-overlay {
            width: 50px;
            height: 50px;
            font-size: 1.4rem;
          }

          .gallery-item-content {
            padding: 16px;
          }

          .gallery-item-content h3 {
            font-size: 1rem;
          }

          .gallery-modal-close {
            top: 15px;
            right: 20px;
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
          }
        }

        @media (max-width: 480px) {
          .gallery-page-title {
            font-size: 1.5rem;
          }

          .gallery-stats {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .gallery-stat-card {
            padding: 14px 10px;
          }

          .gallery-stat-value {
            font-size: 1.3rem;
          }

          .gallery-stat-label {
            font-size: 0.65rem;
          }

          .gallery-filters {
            gap: 8px;
          }

          .gallery-filter-btn {
            padding: 8px 16px;
            font-size: 0.75rem;
            gap: 6px;
          }

          .gallery-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .gallery-item-media {
            height: 200px;
          }

          .gallery-play-overlay {
            width: 45px;
            height: 45px;
            font-size: 1.2rem;
          }

          .gallery-item-badge {
            font-size: 0.6rem;
            padding: 4px 10px;
          }

          .gallery-item-content {
            padding: 14px;
          }

          .gallery-item-content h3 {
            font-size: 0.95rem;
          }

          .gallery-item-album {
            font-size: 0.6rem;
            padding: 3px 10px;
          }

          .gallery-item-date {
            font-size: 0.65rem;
          }

          .gallery-empty {
            padding: 40px 20px;
          }

          .gallery-empty i {
            font-size: 1.5rem;
          }

          .gallery-modal-close {
            top: 10px;
            right: 15px;
            width: 35px;
            height: 35px;
            font-size: 1rem;
          }

          .gallery-modal {
            padding: 10px;
          }

          .gallery-modal-content img,
          .gallery-modal-content video {
            max-width: 95vw;
            max-height: 80vh;
          }
        }

        @media (max-width: 360px) {
          .gallery-stats {
            grid-template-columns: 1fr;
          }

          .gallery-stat-card {
            padding: 12px;
          }

          .gallery-grid {
            grid-template-columns: 1fr;
          }

          .gallery-item-media {
            height: 180px;
          }
        }
      `}</style>
    </div>
  );
};

export default Gallery;